import { useState } from 'react';
import { useCreate, useNotify } from 'react-admin';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { useGateValue } from '@statsig/react-bindings';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import { AuditLog } from '@pumpkincare/shared';

import RoutePaths from '../../../../routes/index.js';
import { getDateAsFormattedLocalTZ } from '../../../../shared/utils';
import {
  MedicalRecordsHistoryTypeEnum,
  MedicalRecordsReviewHistoryActionEnum,
  MedicalRecordsReviewHistoryCreatedByEnum,
  PetMrrStateEnum,
} from '../../constants';
import { medicalRecordsReviewHistoryStyle } from './medical-records-review-history-modal-style.js';

function formatMrrAuditLogs(mrrHistory, showReasonForVisit) {
  const initiator = mrrHistory?.ops_user?.name || mrrHistory?.created_by || 'System';

  let description = `MRR Stated updated from ${
    PetMrrStateEnum.getById(mrrHistory.previous_mrr_state)?.name
  } to ${PetMrrStateEnum.getById(mrrHistory.new_mrr_state)?.name}.`;
  if (mrrHistory.note) {
    description = description + `<br>${mrrHistory.note}`;
  }
  if (
    showReasonForVisit &&
    mrrHistory.new_mrr_state === PetMrrStateEnum.PREVISIT_MRR_TRIGGERED.id
  ) {
    if (mrrHistory.appointment_date) {
      description =
        description +
        `<br><br>Appointment on ${mrrHistory.appointment_date} at ${mrrHistory.appointment_vet}`;
    }
    if (mrrHistory.visit_type) {
      description = description + `<br>Visit type: ${mrrHistory.visit_type}`;
    }
    if (mrrHistory.diagnosis_story) {
      description =
        description + `<br>Reason for visit: ${mrrHistory.diagnosis_story}`;
    }
  }
  return {
    detail: `${initiator} - ${getDateAsFormattedLocalTZ(
      mrrHistory.created_at,
      'M/D/YYYY'
    )}`,
    event: `MRR State Updated to ${
      PetMrrStateEnum.getById(mrrHistory.new_mrr_state)?.name
    }`,
    description,
  };
}

function getEnumId(value) {
  if (PetMrrStateEnum.getById(value)?.id === value) return value;
  const found = PetMrrStateEnum.choices().find(choice => choice.name === value);
  return found ? found.id : value;
}

function MedicalRecordsReviewHistoryModal({
  petId,
  currentOpsUserId,
  onClose,
  onSuccess,
  mrrHistoryList,
  currentState = '',
  claimSubmissionId = '',
}) {
  const classes = medicalRecordsReviewHistoryStyle();
  const notify = useNotify();
  const [create] = useCreate();
  const [toggleLoading, setToggleLoading] = useState(false);

  const hasChangeLog = mrrHistoryList.length > 0;
  const createMrrHistoryApi = `${RoutePaths.pets}/${petId}/mrr-history`;
  const { mvx647PreClaimsMrr } = useFlags() || {};
  const mvx936ReasonForVisit = useGateValue('mvx-936_reason_for_visit');

  const PREVISIT_MRR_IDS = [
    PetMrrStateEnum.PREVISIT_MRR_TRIGGERED.id,
    PetMrrStateEnum.PREVISIT_MRR_AWAITING_RECORDS.id,
    PetMrrStateEnum.PREVISIT_MRR_INFO_UPDATED.id,
  ];
  const TERMINAL_MRR_STATES = [
    PetMrrStateEnum.FULL_HISTORY_REVIEWED.id,
    PetMrrStateEnum.PARTIAL_HISTORY_REVIEWED.id,
    PetMrrStateEnum.NEEDS_REVIEW.id,
    PetMrrStateEnum.MR_REVIEW_NOT_NEEDED.id,
    PetMrrStateEnum.APPEAL_UNDER_REVIEW.id,
  ];

  const normalizedState = (currentState || '').trim().toLowerCase();

  const isPrevisitToggleState =
    PREVISIT_MRR_IDS.includes(normalizedState) &&
    !TERMINAL_MRR_STATES.includes(normalizedState);

  const [toggleChecked, setToggleChecked] = useState(
    normalizedState === PetMrrStateEnum.PREVISIT_MRR_AWAITING_RECORDS.id
  );

  const filteredChoices = PetMrrStateEnum.choices().filter(
    choice => !PREVISIT_MRR_IDS.includes(choice.id)
  );

  const [selectedState, setSelectedState] = useState('');
  const [note, setNote] = useState('');

  function handleSubmit() {
    const previousStateId = getEnumId(currentState);
    const payload = {
      pet_id: petId,
      type: MedicalRecordsHistoryTypeEnum.MRR_STATE,
      new_mrr_state: selectedState,
      created_by: MedicalRecordsReviewHistoryCreatedByEnum.OPS_USER,
      action: MedicalRecordsReviewHistoryActionEnum.UPDATED,
      ops_user_id: currentOpsUserId,
    };
    if (currentState) payload.previous_mrr_state = previousStateId;
    if (note) payload.note = note;
    if (claimSubmissionId) payload.claim_submission_id = claimSubmissionId;

    create(
      createMrrHistoryApi,
      { data: payload },
      {
        onSuccess: mmrHistoryResponseValue => {
          notify('New MRR State created sucessfully.', { type: 'success' });
          onSuccess(mmrHistoryResponseValue);
          onClose();
        },
        onError: ({ message }) => {
          notify(
            message || `There was an error while creating a new MRR State.`,
            'error'
          );
        },
      }
    );
  }

  function handleToggleChange(event) {
    const checked = event.target.checked;
    setToggleChecked(checked);
    setSelectedState('');
    setToggleLoading(true);
    const newStateId = checked
      ? PetMrrStateEnum.PREVISIT_MRR_AWAITING_RECORDS.id
      : PetMrrStateEnum.PREVISIT_MRR_INFO_UPDATED.id;
    const previousStateId = getEnumId(currentState);
    const payload = {
      pet_id: petId,
      type: MedicalRecordsHistoryTypeEnum.MRR_STATE,
      new_mrr_state: newStateId,
      created_by: MedicalRecordsReviewHistoryCreatedByEnum.OPS_USER,
      action: MedicalRecordsReviewHistoryActionEnum.UPDATED,
      ops_user_id: currentOpsUserId,
      previous_mrr_state: previousStateId,
    };
    if (note) payload.note = note;
    if (claimSubmissionId) payload.claim_submission_id = claimSubmissionId;

    create(
      createMrrHistoryApi,
      { data: payload },
      {
        onSuccess: mmrHistoryResponseValue => {
          setToggleLoading(false);
          notify('Toggle action sent successfully.', { type: 'success' });
          onSuccess(mmrHistoryResponseValue);
        },
        onError: ({ message }) => {
          setToggleLoading(false);
          notify(message || `Error sending toggle action.`, 'error');
        },
      }
    );
  }

  function renderToggle() {
    if (!(mvx647PreClaimsMrr && isPrevisitToggleState)) return null;
    return toggleLoading ? (
      <CircularProgress size={28} style={{ marginBottom: '16px' }} />
    ) : (
      <FormControlLabel
        control={
          <Switch
            color='primary'
            checked={toggleChecked}
            onChange={handleToggleChange}
          />
        }
        label='Pre-visit MRR awaiting records'
        style={{ marginBottom: '16px' }}
      />
    );
  }

  return (
    <Dialog open onClose={onClose} classes={{ paper: classes.paper }}>
      <DialogTitle variant='h6' className={classes.modalTitle}>
        MRR Update
      </DialogTitle>
      <DialogContent className={classes.modalContent}>
        <Grid container spacing={2}>
          <Grid item xs={hasChangeLog ? 6 : 12}>
            {renderToggle()}
            <FormControl fullWidth variant='outlined' margin='normal'>
              <InputLabel id='status-label'>Update Pet MRR State</InputLabel>
              <Select
                labelId='status-label'
                value={selectedState}
                onChange={e => setSelectedState(e.target.value)}
                label='Update Pet MRR State'
                displayEmpty
              >
                {filteredChoices?.map(choice => (
                  <MenuItem key={choice.id} value={choice.id}>
                    {choice.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              classes={{ root: classes.textLabel }}
              control={
                <TextField
                  id='reason-for-request'
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  minRows={2}
                  multiline
                  fullWidth
                  placeholder='Add Note (Optional)'
                />
              }
              label=''
              style={{ marginTop: '16px' }}
            />
          </Grid>
          {hasChangeLog ? (
            <Grid item xs={6}>
              <div className={classes.audit}>
                <AuditLog
                  auditLog={mrrHistoryList.map(mrrHistory =>
                    formatMrrAuditLogs(mrrHistory, mvx936ReasonForVisit)
                  )}
                />
              </div>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>

      <DialogActions
        classes={{
          root: classes.modalActions,
          spacing: classes.unsetSpacing,
        }}
      >
        <Button
          className={classes.confirmButton}
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={!selectedState}
        >
          Update
        </Button>
        <Button
          onClick={onClose}
          className={classes.cancelButton}
          variant='outlined'
          color='primary'
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const mrrHistoryListProps = PropTypes.arrayOf(
  PropTypes.shape({
    action: PropTypes.string,
    claim_submission_id: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    created_by: PropTypes.string.isRequired,
    description: PropTypes.string,
    id: PropTypes.string.isRequired,
    new_mrr_state: PropTypes.string.isRequired,
    note: PropTypes.string,
    ops_user: PropTypes.shape({
      active: PropTypes.bool.isRequired,
      created_at: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      updated_at: PropTypes.string,
    }).isRequired,
    ops_user_id: PropTypes.string.isRequired,
    pet_id: PropTypes.string.isRequired,
    previous_mrr_state: PropTypes.string,
    type: PropTypes.string.isRequired,
  })
);

MedicalRecordsReviewHistoryModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  currentState: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  currentOpsUserId: PropTypes.string,
  petId: PropTypes.string,
  claimSubmissionId: PropTypes.string,
  mrrHistoryList: mrrHistoryListProps,
};

export default MedicalRecordsReviewHistoryModal;
